import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PictureGrid from "../components/PictureGrid";
import { selectAccessToken, selectAuthenticated, selectUserData } from "../selectors";
import { useMediaQuery } from "react-responsive";
import FileUpload from "../components/FileUpload";
import ImageViewer2 from "../components/ImageViewer2";
import { fetchExperiments, fetchExperiment } from "../Utils/ExperimentUtils";
import { creditsForImage, generateImage, loadGeneratedImage } from "../Utils/ImageGenerateUtils";
import { fetchUser } from "../Utils/UserUtils";
import { setUserData } from "../store/AuthActions";
import ToastErrorMessage from "../components/ToastErrorMessage";
import { updateAllResults } from "../Utils/ExperimentUtils";
import { Tooltip } from "flowbite-react";
import GalleryModal from "../components/GalleryModal";
import SignInPrompt from "../components/SignInPrompt";
import Joyride, { STATUS } from "react-joyride";
import { useJoyrideStyles } from "../theme";
import { TRYON_SERVER_URL } from "../config";
import { fetchExperimentImage } from "../Utils/ExperimentImageUtils";
import CanvasLoader from "../components/CanvasLoader";
import { InfoIcon } from "lucide-react";

export default function ModelSwapAI({selectedPrompt, setShowPromptGalleryModal, showPromptGalleryModal}) {
    const [modelImage, setModelImage] = useState(null)
    const [selectedModelFile, setSelectedModelFile] = useState(null)
    const [selectedModelObj, setSelectedModelObj] = useState(null)
    const [selectedMaskFile, setSelectedMaskFile] = useState(null)
    const [selectedMaskObj, setSelectedMaskObj] = useState(null)
    const [outputImage, setOutputImage] = useState(null)
    const [prompt, setPrompt] = useState(selectedPrompt ? selectedPrompt : "")
    const accessToken = useSelector(selectAccessToken) || null;
    const dispatch = useDispatch();
    const userData = JSON.parse(useSelector(selectUserData) || "null");
    const reimagineCredits = creditsForImage("2K", 1);
    const [isGenerating, setIsGenerating] = useState(false);
    const [resultPreview, setResultPreview] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorStatus, setErrorStatus] = useState('');
    const authenticated = useSelector(selectAuthenticated);
    const [experiments, setExperiments] = useState([]);
    const [experimentsFetched, setExperimentsFetched] = useState(false);
    const [fetchingExperiments, setFetchingExperiments] = useState({});
    const [advancedOptions, setAdvancedOptions] = useState(false);
    const navigate = useNavigate();
    const [fetchingExperimentsFromServer, setFetchingExperimentsFromServer] = useState({});

    const [seed, setSeed] = useState(-1);
    const [guidanceScale, setGuidanceScale] = useState(7.5);
    const [strength, setStrength] = useState(0.99);
    const [inferenceSteps, setInferenceSteps] = useState(20);
    const [numResults, setNumResults] = useState(2);

    const handleSeedChange = (e) => setSeed(Number(e.target.value));
    const handleGuidanceScaleChange = (e) => setGuidanceScale(Number(e.target.value));
    const handleStrengthChange = (e) => setStrength(Number(e.target.value));
    const handleInferenceStepsChange = (e) => setInferenceSteps(Number(e.target.value));
    const handleNumResultsChange = (e) => setNumResults(Number(e.target.value));
    const isMobile = useMediaQuery({ query: `(max-width: 760px)` })
    const [showMaskEditor, setShowMaskEditor] = useState(false);
    const [binaryMaskImage, setBinaryMaskImage] = useState(null);
    const [showImageViewer, setShowImageViewer] = useState(false);
    const [imageViewerImage, setImageViewerImage] = useState(null);
    const [showToastErrorMessage, setShowToastErrorMessage] = useState(false);
    const [pendingOrRunningExperimentIds, setPendingOrRunningExperimentIds] = useState([]);
    const ongoingRequestsForExperimentStatus = new Set();
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [selectingType, setSelectingType] = useState("image");

    useEffect(() => {
        console.log("accessToken:", accessToken)
        console.log("authenticated:", authenticated)

        if (!experimentsFetched) {
            console.log("fetching experiments");
            if(accessToken && authenticated){
                fetchExperiments1().then(() => {
                    console.log("experiments fetched");
                })
            }
        }

        // experiments.map((experiment) => {
        //     if (experiment.status === "pending" || experiment.status === "running") {
        //         if (!fetchingExperiments[experiment.id]) {
        //             console.log("setTimeout", experiment.id)
        //             setTimeout(fetchExperiment1, 5000, experiment);
        //         }
        //     }
        // })
    }, [experiments, accessToken, authenticated]);

    const fetchExperimentStatus = async (experimentId) => {
        if(!ongoingRequestsForExperimentStatus.has(experimentId)){
            ongoingRequestsForExperimentStatus.add(experimentId);

            try{
                console.log("fetching experiment status for", experimentId);
                const response = await fetchExperiment(experimentId, accessToken);
                console.log("experiment status response", response);
                if(response.status === "success"){
                    console.log("experiment status fetched for", experimentId);
                    
                    // update experiment results
                    const experiment = response.data.experiment;
                    console.log("experiment status:", experiment.status);
                    if(experiment.status === "completed" || experiment.status === "failed"){
                        
                        if(experiment.status === "failed"){
                            setShowToastErrorMessage(true);
                            setErrorMessage("Experiment Failed. Please Try Again!");
                            setErrorStatus("experiment failed");
                        }

                        setPendingOrRunningExperimentIds((prevExperimentIds) => prevExperimentIds.filter((id) => id !== experimentId));
                        setExperiments((prevExperiments) => updateAllResults(experiment, prevExperiments));
                    }
                }
            }catch(error){
                console.error("Error fetching experiment status:", error);
            }finally{
                ongoingRequestsForExperimentStatus.delete(experimentId);
            }
        }
    }

    useEffect(() => {
        console.log("pending or running experiment ids", pendingOrRunningExperimentIds);
        const fetchExperimentStatuses = async () => {
            console.log("fetching experiment statuses:", pendingOrRunningExperimentIds);
            pendingOrRunningExperimentIds.forEach((experimentId) => {
                fetchExperimentStatus(experimentId);
            });
        }

        fetchExperimentStatuses();

        let interval = null;
        if(pendingOrRunningExperimentIds.length > 0){
            interval = setInterval(() => {
                fetchExperimentStatuses();
            }, 5000);
        }

        return () => {
            if(interval !== null){
                clearInterval(interval);
            }
            ongoingRequestsForExperimentStatus.clear();
        }
    }, [pendingOrRunningExperimentIds]);

    useEffect(() => {
        const id = new URLSearchParams(window.location.search).get('id');
        console.log("id found in url", id);
        if(id){
            console.log("id found in url", id);
            fetchExperimentImage(id, accessToken).then(async (response) => {
                if(response.status === "success"){
                    console.log("experiment image fetched", response.data);
                    let experiment_image = response.data;
                    setSelectedModelObj(experiment_image);
                    // convert image_url to file
                    const imageUrl = `${TRYON_SERVER_URL}/${experiment_image.image_url}`;
                    console.log("image url", imageUrl);
                    const imageBlob = await fetch(imageUrl).then(res => res.blob());
                    const imageFile = new File([imageBlob], "image.png", { type: "image/png" });
                    setSelectedModelFile(imageFile);
                    setModelImage(URL.createObjectURL(imageFile));
                    console.log("model image", experiment_image, imageFile);
                }else{
                    console.error("Failed to fetch experiment image");
                }
            });
        }
    }, []);

    const handleErrorClose = () => {
        setShowToastErrorMessage(false);
        setErrorMessage("");
        setErrorStatus("");
    }

    const handleModelImageSelected = async (event) => {
        console.log("model image selected", event)
        const files = event.target.files;
        console.log("file types in fm", files)

        if (files.length > 0) {
            console.log(files[0])
            setModelImage(URL.createObjectURL(files[0]))
            setSelectedModelFile(files[0])
        }
    };

    const handleMaskImageSelected = async (event) => {
        console.log("mask image selected", event)
        const files = event.target.files;
        console.log("file types in fm", files)

        if (files.length > 0) {
            console.log(files[0])
            setBinaryMaskImage(URL.createObjectURL(files[0])) 
            setSelectedMaskFile(files[0])
        }
    };

    const handleLoginRedirect = () => {
        navigate("/signin");
    }

    const fetchExperiments1 = async () => {
        setFetchingExperimentsFromServer(true);
        const response = await fetchExperiments({aimodel: "reimagine_ai", access: "private"}, accessToken)
        if(response.status === "success"){
            setExperiments(response.data);
            setExperimentsFetched(true);
            setFetchingExperimentsFromServer(false);
        }else{
            setFetchingExperimentsFromServer(false);
            console.error("Failed to fetch experiments");
        }
    }

    const sourceImageFile = async () => {
        if (selectedModelFile instanceof File) {
            return selectedModelFile;
        }
        const rawUrl = selectedModelObj?.image_url
            ? (selectedModelObj.image_url.startsWith("http")
                ? selectedModelObj.image_url
                : `${TRYON_SERVER_URL}/${selectedModelObj.image_url}`)
            : modelImage;
        if (!rawUrl) {
            return null;
        }
        const response = await fetch(rawUrl);
        if (!response.ok) {
            throw new Error("Could not read the source image");
        }
        const blob = await response.blob();
        return new File([blob], "image.png", { type: blob.type || "image/png" });
    };

    async function handleGenerateClick(e) {
        e?.preventDefault?.();
        if (!authenticated) {
            navigate("/signin");
            return;
        }
        if (!String(prompt).trim() || (!selectedModelFile && !selectedModelObj && !modelImage)) {
            setShowToastErrorMessage(true);
            setErrorMessage("Upload a photo and enter a prompt.");
            setErrorStatus("Error!");
            return;
        }
        const balance = Number(userData?.credits);
        if (!Number.isFinite(balance) || balance < reimagineCredits) {
            navigate(`/credits?buy_credits=true&num_of_credits=${reimagineCredits}`);
            return;
        }

        setIsGenerating(true);
        try {
            const file = await sourceImageFile();
            if (!file) {
                setShowToastErrorMessage(true);
                setErrorMessage("Upload a photo and enter a prompt.");
                setErrorStatus("Error!");
                return;
            }
            const result = await generateImage({
                prompt,
                resolution: "2K",
                n: 1,
                images: [file],
                accessToken,
            });
            if (result.status !== "success" || !result.data?.download_url) {
                setErrorStatus(result.statusCode || "Error");
                setErrorMessage(result.message || "Failed to reimagine the image");
                setShowToastErrorMessage(true);
                return;
            }
            const previewUrl = await loadGeneratedImage(result.data.download_url, accessToken);
            setResultPreview(previewUrl);
            setPrompt("");
            const user = await fetchUser(accessToken);
            if (user.status === "success") {
                dispatch(setUserData(JSON.stringify(user.data)));
            }
        } catch (error) {
            console.error("Error reimagining image:", error);
            setShowToastErrorMessage(true);
            setErrorStatus("Error!");
            setErrorMessage(error?.message || "Something went wrong. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    }

    function handlePromptChange(e) {
        setPrompt(e.target.value)
    }

    const toggleAdvancedOptions = () => {
        setAdvancedOptions((prev) => !prev);
    }

    const fetchExperiment1 = async (experiment) => {
        if (!fetchingExperiments[experiment.id]) {
            let fetchingExperiments1 = { ...fetchingExperiments }; // Clone the state object to avoid direct mutation
            fetchingExperiments1[experiment.id] = true;
            setFetchingExperiments(fetchingExperiments1);

            const response = await fetchExperiment(experiment.id, accessToken)
            if(response.status === "success"){
                let fetchedExperiment = response.data;
                console.log("Fetched experiment:", fetchedExperiment);

                if (fetchedExperiment.status === 'completed' || fetchedExperiment.status === 'failed') {
                    console.log("Final status reached:", fetchedExperiment.status);

                    setExperiments((prevExperiments) =>
                        prevExperiments.map((experiment1) =>
                            experiment1.id === fetchedExperiment.id ? { ...experiment1, ...fetchedExperiment } : experiment1
                        )
                    );
                } else if (fetchedExperiment.status === 'pending' || fetchedExperiment.status === 'running') {
                    console.log("Experiment is still running, polling again...");
                    setTimeout(() => fetchExperiment1(experiment), 5000);
                }
            }else{
                console.error("Failed to fetch experiment");
            }

            fetchingExperiments1[experiment.id] = false;
            setFetchingExperiments(fetchingExperiments1);
        }
    };

    const handleImageClick = (image) => {
        setShowImageViewer(true);
        setImageViewerImage(image);
    }

    const hideToastErrorMessage = () => {
        setShowToastErrorMessage(false);
        setErrorMessage("");
        setErrorStatus("");
    }

    const handleCreateMaskManually = () => {
        //hide error message if it is shown
        hideToastErrorMessage();
        if(selectedModelFile !== null || selectedModelObj !== null){
            setShowMaskEditor(true);
            setSelectedMaskFile(null);
            setBinaryMaskImage(null);
        }else{
            setShowToastErrorMessage(true);
            setErrorMessage("Please upload an image first.");
            setErrorStatus("Error!");
            setShowMaskEditor(false);
        }
    }

    const handleSelectFromLibrary = (type) => {
        setSelectingType(type);
        setShowGalleryModal(true);
    }

    const steps = [
        {
            target: ".select_image",
            content: "Upload an image or select from library",
            disableBeacon: true,
        },
        {
            target: ".prompt",
            content: "Describe how the photo should change",
            disableBeacon: true,
        },
        {
            target: ".browse_prompt_gallery",
            content: "Click here to browse the prompt gallery",
            disableBeacon: true,
        },
        {
            target: ".generate",
            content: "Click the generate button to generate the images",
            disableBeacon: true,
        },
        {
            target: ".output",
            content: "The generated images will be displayed here",
            disableBeacon: true,
        }
    ]

    const tourStyles = useJoyrideStyles();
    const handleJoyrideCallback = (data) => {
        const { step, status } = data;
        console.log(step, status)

        if ([STATUS.FINISHED].includes(status)) {
            localStorage.setItem("REIMAGINEAI_JOYRIDE_STATUS", "finished")
        }
    };

    useEffect(() => {
        console.log("selectedPrompt:", selectedPrompt)
        if(selectedPrompt){
            setPrompt(selectedPrompt)
        }
    }, [selectedPrompt])

    return (
        <>
        {localStorage.getItem("REIMAGINEAI_JOYRIDE_STATUS") !== "finished" ? (
                <Joyride callback={handleJoyrideCallback} steps={steps} styles={tourStyles} showProgress={true} showSkipButton={true}
                    continuous={true} />) : (<></>)}
        <div className="grid h-[calc(100vh-5rem)] w-full grid-cols-1 gap-4 overflow-y-auto bg-ground p-4 selection:bg-primary-100 lg:grid-cols-12 lg:overflow-hidden lg:min-h-0">
            <div className="flex min-h-[28rem] flex-col overflow-y-auto rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] lg:col-span-5 lg:h-full lg:min-h-0">
                {isMobile ? (
                    <p className="mb-3 font-Mulish font-semibold text-lg text-secondary">Reimagine</p>
                ) : null}

                <div className="select_image">
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="font-Mulish font-semibold text-lg text-secondary">Photo</h2>
                        <Tooltip content="Upload a photo or choose one from your gallery" className="max-w-[200px] text-xs">
                            <InfoIcon className="h-4 w-4 cursor-pointer text-graphite" />
                        </Tooltip>
                    </div>
                    <div className="rounded-2xl bg-primary-50 p-2">
                        {modelImage ? (
                            <div className="relative">
                                <img
                                    src={modelImage}
                                    className="h-80 w-full cursor-pointer rounded-lg object-cover object-top"
                                    alt="Source photo"
                                    onClick={() => handleImageClick(modelImage)}
                                />
                                <button
                                    type="button"
                                    aria-label="Remove photo"
                                    onClick={() => {
                                        setModelImage(null);
                                        setSelectedModelFile(null);
                                        setSelectedModelObj(null);
                                    }}
                                    className="absolute right-2 top-2 rounded-full border border-hairline bg-white p-2"
                                >
                                    <svg className="h-4 w-4 text-primary-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        ) : (
                            <FileUpload key1="image" handleFileSelected={handleModelImageSelected} buttonText="Choose from gallery" handleClick={() => handleSelectFromLibrary("image")} />
                        )}
                    </div>
                    {modelImage ? (
                        <p className="mt-2 font-Mulish text-sm text-graphite">
                            {selectedModelObj ? "Selected from gallery" : "Selected from files"}
                        </p>
                    ) : null}
                </div>

                <div className="prompt mt-4">
                    <label className="mb-2 flex items-center justify-between font-Mulish text-sm text-graphite">
                        Prompt
                        <Tooltip content="Describe how the photo should change" className="max-w-[200px] text-xs">
                            <InfoIcon className="h-4 w-4 cursor-pointer text-graphite" />
                        </Tooltip>
                    </label>
                    <textarea
                        name="prompt"
                        rows="3"
                        className="w-full rounded-lg border border-hairline bg-white px-3 py-2 font-Mulish text-sm text-secondary outline-none focus:ring-2 focus:ring-primary-200"
                        placeholder="Describe the change. For example, a close portrait with studio lighting."
                        value={prompt}
                        onChange={handlePromptChange}
                    />
                </div>

                <button
                    type="button"
                    onClick={() => showPromptGalleryModal("all")}
                    className="browse_prompt_gallery mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2.5 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                    </svg>
                    Browse prompt gallery
                </button>

                <button
                    type="button"
                    className={`generate mt-3 w-full rounded-full px-4 py-2.5 font-Mulish font-semibold text-sm ${isGenerating ? "cursor-not-allowed bg-primary-100 text-primary-800" : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"}`}
                    onClick={handleGenerateClick}
                    disabled={isGenerating}
                >
                    {isGenerating ? "Reimagining..." : `Reimagine (${reimagineCredits} credits)`}
                </button>
            </div>

            <div className="output flex min-h-[20rem] flex-col rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] lg:col-span-7 lg:h-full lg:min-h-0 lg:overflow-hidden">
                <h2 className="mb-3 shrink-0 font-Mulish font-semibold text-lg text-secondary">Result</h2>
                {authenticated === null ? (
                    <div className="flex min-h-[20rem] flex-1 items-center justify-center rounded-2xl bg-primary-50">
                        <CanvasLoader title="Loading earlier edits" />
                    </div>
                ) : authenticated ? (
                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
                        {(isGenerating || resultPreview || (fetchingExperimentsFromServer && !experimentsFetched) || experiments.length === 0) && (
                        <div className="flex min-h-[16rem] flex-1 items-center justify-center overflow-hidden rounded-2xl bg-primary-50 p-4">
                            {isGenerating ? (
                                <CanvasLoader title="Reimagining" />
                            ) : resultPreview ? (
                                <img
                                    src={resultPreview}
                                    alt="Reimagined"
                                    className="max-h-full max-w-full cursor-pointer rounded-lg object-contain"
                                    onClick={() => handleImageClick(resultPreview)}
                                />
                            ) : fetchingExperimentsFromServer && !experimentsFetched ? (
                                <CanvasLoader title="Loading earlier edits" />
                            ) : (
                                <div className="space-y-2 px-4 text-center">
                                    <div className="mx-auto h-10 w-10 text-primary-700">
                                        <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <p className="font-Mulish font-semibold text-lg text-secondary">No image yet</p>
                                    <p className="font-Mulish text-sm text-graphite">Your edit will show up here</p>
                                </div>
                            )}
                        </div>
                        )}
                        {experimentsFetched && experiments.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {experiments.map((experiment) => (
                                    <PictureGrid key={experiment.id} experiment={experiment} />
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <SignInPrompt
                        title="Sign in to see your edit"
                        message="Your reimagined photo will show up here."
                        onSignIn={handleLoginRedirect}
                        className="min-h-[20rem] flex-1 px-6 py-10"
                    />
                )}
            </div>

            {showImageViewer && imageViewerImage && (
                <ImageViewer2 image_url={imageViewerImage} onClose={() => setShowImageViewer(false)} />
            )}

            {showToastErrorMessage && (
                <ToastErrorMessage
                    errorStatus={errorStatus}
                    errorMessage={errorMessage}
                    handleErrorClose={() => setShowToastErrorMessage(false)}
                />
            )}

            {showGalleryModal && (
                <GalleryModal
                    setImage={(url) => {
                        setSelectedModelFile(null);
                        setModelImage(url);
                    }}
                    setSelectedObj={setSelectedModelObj}
                    setShowGalleryModal={setShowGalleryModal}
                />
            )}
        </div>
        </>
    );
}
